import React, { useState } from 'react';

const THEMES = [
  'Governance',
  'Leadership',
  'Financial Management',
  'Welfare',
  'Places (Facilities)',
  'Athlete & Runner Offer',
  'Performance & Pathways',
  'Volunteers',
  'Marketing & Communications',
  'Sustainability & Wider Impact',
];

const PROGRESS_STATUSES = ['Planning', 'In Progress', 'On Track', 'At Risk', 'Completed'];

export default function ThemesSection({ initialThemes, onSave }) {
  const [themes, setThemes] = useState(
    initialThemes || Object.fromEntries(THEMES.map((t) => [t, []]))
  );

  const [expandedThemes, setExpandedThemes] = useState({});
  const [editingObjective, setEditingObjective] = useState(null);
  const [formData, setFormData] = useState(null);

  const toggleTheme = (theme) => {
    setExpandedThemes((prev) => ({
      ...prev,
      [theme]: !prev[theme],
    }));
  };

  const startAddObjective = (theme) => {
    setEditingObjective({ theme, mode: 'add', id: null });
    setFormData({
      objective: '',
      tasksActivities: '',
      responsibility: '',
      timescales: '',
      finance: '',
      progressStatus: 'Planning',
    });
  };

  const startEditObjective = (theme, objective) => {
    setEditingObjective({ theme, mode: 'edit', id: objective.id });
    setFormData(objective);
  };

  const cancelEdit = () => {
    setEditingObjective(null);
    setFormData(null);
  };

  const saveObjective = () => {
    if (!formData.objective.trim()) {
      alert('Objective name is required');
      return;
    }

    const newThemes = { ...themes };
    const themeList = newThemes[editingObjective.theme];

    if (editingObjective.mode === 'add') {
      const newObjective = {
        id: Date.now().toString(),
        ...formData,
      };
      newThemes[editingObjective.theme] = [...themeList, newObjective];
    } else {
      const index = themeList.findIndex((obj) => obj.id === editingObjective.id);
      if (index !== -1) {
        themeList[index] = { id: editingObjective.id, ...formData };
      }
    }

    setThemes(newThemes);
    if (onSave) {
      onSave(newThemes);
    }
    cancelEdit();
  };

  const deleteObjective = (theme, objectiveId) => {
    if (!window.confirm('Are you sure you want to delete this objective?')) {
      return;
    }

    const newThemes = {
      ...themes,
      [theme]: themes[theme].filter((obj) => obj.id !== objectiveId),
    };
    setThemes(newThemes);
    if (onSave) {
      onSave(newThemes);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'Planning': 'bg-slate-200 text-slate-800',
      'In Progress': 'bg-blue-200 text-blue-800',
      'On Track': 'bg-green-200 text-green-800',
      'At Risk': 'bg-orange-200 text-orange-800',
      'Completed': 'bg-green-600 text-on-primary',
    };
    return colors[status] || 'bg-surface-container text-on-surface';
  };

  return (
    <div className="space-y-3">
      {THEMES.map((theme) => (
        <div key={theme} className="border border-outline-variant rounded-lg overflow-hidden">
          {/* Theme Header */}
          <button
            onClick={() => toggleTheme(theme)}
            className="w-full flex items-center justify-between p-4 bg-surface-container hover:bg-surface-container-high transition-colors"
          >
            <div className="flex items-center gap-3">
              <h4 className="font-semibold text-on-surface">{theme}</h4>
              <span className="badge bg-primary-fixed text-on-primary-fixed text-xs px-2 py-1 rounded">
                {themes[theme].length}
              </span>
            </div>
            <span className="text-lg">{expandedThemes[theme] ? '▼' : '▶'}</span>
          </button>

          {/* Theme Content */}
          {expandedThemes[theme] && (
            <div className="px-4 py-4 border-t border-outline-variant space-y-3">
              {/* Objectives List */}
              {themes[theme].length > 0 && (
                <div className="space-y-2">
                  {themes[theme].map((objective) => (
                    <div key={objective.id} className="p-3 bg-surface-container rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-on-surface">{objective.objective}</p>
                          <p className="text-sm text-on-surface-variant mt-1">{objective.tasksActivities}</p>
                        </div>
                        <div className="flex gap-2 ml-2 flex-shrink-0">
                          <button
                            onClick={() => startEditObjective(theme, objective)}
                            className="p-1 hover:bg-surface-container-high rounded transition-colors text-primary"
                            title="Edit objective"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deleteObjective(theme, objective.id)}
                            className="p-1 hover:bg-surface-container-high rounded transition-colors text-error"
                            title="Delete objective"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-on-surface-variant">
                        {objective.responsibility && (
                          <div>
                            <span className="font-semibold">Responsibility:</span> {objective.responsibility}
                          </div>
                        )}
                        {objective.timescales && (
                          <div>
                            <span className="font-semibold">Timescales:</span> {objective.timescales}
                          </div>
                        )}
                        {objective.finance && (
                          <div>
                            <span className="font-semibold">Finance:</span> £{parseFloat(objective.finance).toFixed(2)}
                          </div>
                        )}
                        {objective.progressStatus && (
                          <div>
                            <span className={`badge ${getStatusBadgeColor(objective.progressStatus)}`}>
                              {objective.progressStatus}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Objective Form */}
              {editingObjective?.theme === theme && editingObjective?.mode === 'add' && (
                <ObjectiveForm
                  formData={formData}
                  onFormChange={handleFormChange}
                  onSave={saveObjective}
                  onCancel={cancelEdit}
                  isEditing={false}
                />
              )}

              {/* Edit Objective Form */}
              {editingObjective?.theme === theme && editingObjective?.mode === 'edit' && (
                <ObjectiveForm
                  formData={formData}
                  onFormChange={handleFormChange}
                  onSave={saveObjective}
                  onCancel={cancelEdit}
                  isEditing={true}
                />
              )}

              {/* Add Objective Button */}
              {(!editingObjective || editingObjective.theme !== theme) && (
                <button
                  onClick={() => startAddObjective(theme)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-primary hover:bg-surface-container rounded transition-colors font-medium text-sm"
                >
                  ➕ Add Objective
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ObjectiveForm({ formData, onFormChange, onSave, onCancel, isEditing }) {
  return (
    <div className="p-4 bg-primary-fixed bg-opacity-10 rounded-lg space-y-3 border border-primary-fixed">
      <div className="flex items-center justify-between mb-2">
        <h5 className="font-semibold text-on-surface">
          {isEditing ? 'Edit Objective' : 'New Objective'}
        </h5>
        <button
          onClick={onCancel}
          className="p-1 hover:bg-surface-container rounded transition-colors"
          title="Close form"
        >
          ✕
        </button>
      </div>

      <div>
        <label className="block text-sm font-semibold text-on-surface mb-1">Objective *</label>
        <input
          type="text"
          value={formData.objective}
          onChange={(e) => onFormChange('objective', e.target.value)}
          placeholder="What needs to be achieved?"
          className="w-full px-3 py-2 border border-outline rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-on-surface bg-surface"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-on-surface mb-1">Tasks & Activities</label>
        <textarea
          value={formData.tasksActivities}
          onChange={(e) => onFormChange('tasksActivities', e.target.value)}
          placeholder="Specific actions and activities..."
          className="w-full px-3 py-2 border border-outline rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-on-surface bg-surface resize-none"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-on-surface mb-1">Responsibility</label>
          <input
            type="text"
            value={formData.responsibility}
            onChange={(e) => onFormChange('responsibility', e.target.value)}
            placeholder="Person or team responsible"
            className="w-full px-3 py-2 border border-outline rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-on-surface bg-surface"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-on-surface mb-1">Timescales</label>
          <input
            type="text"
            value={formData.timescales}
            onChange={(e) => onFormChange('timescales', e.target.value)}
            placeholder="e.g., 6 months, Q4 2026"
            className="w-full px-3 py-2 border border-outline rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-on-surface bg-surface"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-on-surface mb-1">Finance (£)</label>
          <input
            type="number"
            value={formData.finance}
            onChange={(e) => onFormChange('finance', e.target.value)}
            placeholder="0.00"
            className="w-full px-3 py-2 border border-outline rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-on-surface bg-surface"
            step="0.01"
            min="0"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-on-surface mb-1">Progress Status</label>
          <select
            value={formData.progressStatus}
            onChange={(e) => onFormChange('progressStatus', e.target.value)}
            className="w-full px-3 py-2 border border-outline rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-on-surface bg-surface"
          >
            {PROGRESS_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded-lg border border-outline text-on-surface hover:bg-surface-container transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          className="btn-primary text-sm px-4 py-2"
        >
          {isEditing ? 'Update' : 'Save'} Objective
        </button>
      </div>
    </div>
  );
}
